import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { Subscription, catchError, combineLatest, debounceTime, filter, of, take } from 'rxjs';
import { OrdinalPipe } from 'src/app/pipes/ordinal/ordinal.pipe';
import { SmallNumberPipe } from 'src/app/pipes/small-number/small-number.pipe';
import { AuthService } from 'src/app/services/auth-service/auth-service.service';
import { JsonReaderService } from 'src/app/services/json-reader/json-reader.service';
import { MetaItemsNewService } from 'src/app/services/meta-items-new/meta-items-new.service';
import { UserSettingsService } from 'src/app/services/user-settings/user-settings.service';
import { ChartComponent } from 'src/app/shared/components/chart/chart.component';
import { CheckPriceButtonComponent } from 'src/app/shared/components/check-price-button/check-price-button.component';
import { CopyTextButtonComponent } from 'src/app/shared/components/copy-text-button/copy-text-button.component';
import { MetaItemsForm } from 'src/app/shared/components/forms/interfaces/meta-items-form';
import { ItemImageComponent } from 'src/app/shared/components/item-image/item-image.component';
import { getAllSlotTypes } from 'src/app/shared/constants/slot-types.constant';
import { ChartData } from 'src/app/shared/models/chart.models';
import { ShopCategory, ShopSubCategory } from 'src/app/shared/models/items.model';
import { FightSize, GetAllFightSizes } from 'src/app/shared/models/meta-models/fight-size';
import { GearUseRecordResponse } from 'src/app/shared/models/meta-models/gear-use-record-response';
import { GearUseSort, GetAllGearUseSorts } from 'src/app/shared/models/meta-models/gear-use-sort';
import { GetAllKillEventPlayerRoles, KillEventPlayerRole } from 'src/app/shared/models/meta-models/kill-event-player-role';
import { SlotType } from 'src/app/shared/models/slot-type.model';

@Component({
  selector: 'app-meta-tems-new',
  imports: [
    CommonModule,
    MatCardModule,
    MatTableModule,
    ReactiveFormsModule,
    MatIconModule,
    FormsModule,
    NgxMatSelectSearchModule,
    MatSelectModule,
    MatButtonModule,
    MatTooltipModule,
    OrdinalPipe,
    SmallNumberPipe,
    MatProgressBarModule,
    MatTableModule,
    CopyTextButtonComponent,
    CheckPriceButtonComponent,
    MatInputModule,
    ChartComponent,
    ItemImageComponent,
  ],
  templateUrl: './meta-tems-new.component.html',
  styleUrl: './meta-tems-new.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MetaTemsNewComponent implements OnInit, OnDestroy, AfterViewInit {
  // MARK: Properties
  form!: FormGroup<MetaItemsForm>;
  subFilterControl: FormControl<string | null> = new FormControl<string | null>(null);
  shopCategories: ShopCategory[] = [];
  filteredShopSubCategories: ShopSubCategory[] = [];
  get shopSubCategoriesCount(): number {
    return this.shopCategories.flatMap((x) => x.subCategories).length;
  }
  slotTypes = getAllSlotTypes();
  killEventPlayerRoles = GetAllKillEventPlayerRoles();
  fightSizes = GetAllFightSizes();
  tiers = [1, 2, 3, 4, 5, 6, 7, 8];
  enchantments = [0, 1, 2, 3, 4];
  sorts = GetAllGearUseSorts();
  subscriptions: Subscription[] = [];
  disableUpdate: boolean = false;
  isLoading: boolean = false;

  chartData: { data: ChartData[]; name: string }[] = [];

  displayedColumns: string[] = [
    'item',
    'totalItemCount',
    'totalPlayerCount',
    'totalEventCount',
    'totalKillerCount',
    'totalAssistantCount',
    'totalVictimCount',
    'killDeathRatio',
    'totalKillFame',
    'averageKillFame',
    'totalDeathFame',
    'averageDeathFame',
    'totalDamageDone',
    'averageDamageDone',
    'totalSupportHealingDone',
    'averageSupportHealingDone',
  ];
  dataSource = new MatTableDataSource<GearUseRecordResponse>([]);

  // MARK: Constructor
  constructor(
    public userSettingsService: UserSettingsService,
    private metaItemsNewService: MetaItemsNewService,
    private jsonReaderService: JsonReaderService,
    public authService: AuthService,
    private cdr: ChangeDetectorRef
  ) {
    this.form = new FormGroup<MetaItemsForm>({
      server: new FormControl<string | null>(null),
      days: new FormControl<number | null>(null),
      limit: new FormControl<number | null>(null),
      slotTypes: new FormControl<SlotType[] | null>(null),
      shopsubcategories: new FormControl<ShopSubCategory[] | null>(null),
      killEventPlayerRoles: new FormControl<KillEventPlayerRole[] | null>(null),
      fightSizes: new FormControl<FightSize[] | null>(null),
      tiers: new FormControl<number[] | null>(null),
      enchantments: new FormControl<number[] | null>(null),
      minTotalItemCount: new FormControl<number | null>(0),
      sorts: new FormControl<GearUseSort[] | null>(null),
    });

    this.jsonReaderService.ensureLoaded();

    this.subscriptions.push(
      // loading categories
      combineLatest([this.jsonReaderService.shopCategoriesData$, this.jsonReaderService.itemsData$]).subscribe(([categoryData, itemsData]) => {
        this.shopCategories = categoryData;
        this.shopCategories.forEach((x) => {
          x.subCategories = x.subCategories.filter((x) => itemsData.some((y) => y.shopSubCategory === x.name && !!y.slotType));
        });
        this.shopCategories = this.shopCategories.filter((x) => x.subCategories.length > 0);
        this.filteredShopSubCategories = this.shopCategories.flatMap((x) => x.subCategories);
        this.cdr.markForCheck();
      }),
      // filtering sub categories
      this.subFilterControl.valueChanges.pipe(debounceTime(100)).subscribe((value) => {
        this.updateFilteredShopSubCategories(value);
        this.cdr.markForCheck();
      }),
      // filtering sub categories when selection changes
      this.form.controls.shopsubcategories.valueChanges.subscribe((value) => {
        this.updateFilteredShopSubCategories(this.subFilterControl.value);
        this.cdr.markForCheck();
      }),
      //catch server changes
      this.userSettingsService.userServer$.subscribe((server) => {
        this.form.controls.server.setValue(server);
        this.update();
        this.cdr.markForCheck();
      }),

      this.authService.user$.pipe(filter((user) => !!user)).subscribe(() => {
        this.metaItemsNewService.getKillEventsCounters(28).subscribe((data) => {
          const serverGroups: { [key: string]: ChartData[] } = {};

          data.forEach((item) => {
            if (!serverGroups[item.server]) {
              serverGroups[item.server] = [];
            }
            serverGroups[item.server].push({
              date: item.dateTime.getTime(),
              value: item.eventCount,
            });
          });

          Object.entries(serverGroups).forEach(([server, items]) => {
            this.chartData.push({ data: items, name: server });
          });
          this.cdr.markForCheck();
        });
      })
    );
  }

  // MARK: Lifecycle
  ngOnInit(): void {}

  ngAfterViewInit() {
    this.subscriptions.push();
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((x) => x.unsubscribe());
  }

  // MARK: Methods
  updateFilteredShopSubCategories(value: string | null) {
    this.filteredShopSubCategories = Array.from(
      new Set([
        ...(this.form.controls.shopsubcategories.value ?? []),
        ...this.shopCategories
          .flatMap((x) => x.subCategories)
          .filter((subCategory) => {
            return subCategory.displayName.toLowerCase().includes(value?.toLowerCase() || '');
            // return subCategory.displayName.toLowerCase().includes(value?.toLowerCase() || '') || subCategory.shopCategory.displayName.toLowerCase().includes(value?.toLowerCase() || '');
          }),
      ])
    );
  }

  update(): void {
    this.isLoading = true;
    this.dataSource.data = [];
    if (!this.userSettingsService.isLoggedIn) {
      return;
    }
    this.disableUpdate = true;
    this.cdr.markForCheck();
    const metaObservable = this.metaItemsNewService
      .getMetaItems(
        this.form.value.server ?? this.userSettingsService.getUserServer(),
        undefined,
        undefined,
        this.form.value.slotTypes,
        this.form.value.shopsubcategories,
        this.form.value.killEventPlayerRoles,
        this.form.value.fightSizes,
        this.form.value.tiers,
        this.form.value.enchantments,
        this.form.value.sorts,
        this.form.value.minTotalItemCount ?? 0
      )
      .pipe(take(1));

    combineLatest([metaObservable, this.jsonReaderService.hasLoaded$])
      .pipe(
        filter(([data, hasLoaded]) => hasLoaded === true),
        catchError((error) => {
          console.error('Error occurred:', error);
          this.isLoading = false;
          return of([]);
        })
      )
      .subscribe(([data, hasLoaded]) => {
        this.dataSource.data = data;
        this.isLoading = false;
        this.cdr.markForCheck();
      });

    setTimeout(() => {
      this.disableUpdate = false;
      this.cdr.markForCheck();
    }, 7000);
  }

  getCountString(control: FormControl, max: number): string {
    if (!control.value || control.value.length === 0) {
      return '(All)';
    }
    return `(${control.value.length}/${max})`;
  }

  noCompareFunction() {
    return 0;
  }

  selectAllSubCats(category: ShopCategory) {
    const selectedValues = this.form.controls.shopsubcategories.value || [];
    for (let sub of category.subCategories) {
      if (!selectedValues.includes(sub)) {
        selectedValues.push(sub);
      }
    }
    this.form.controls.shopsubcategories.setValue(selectedValues);
  }
  removeAllSubCats(category: ShopCategory) {
    const a = this.form.controls.shopsubcategories.value?.filter((x: ShopSubCategory) => !category.subCategories.includes(x));
    this.form.controls.shopsubcategories.setValue(a ?? null);
  }

  showCategoryGroup(category: ShopCategory): boolean {
    return this.filteredShopSubCategories.some((x) => category.subCategories.includes(x));
  }
}
